// src/context/CartContext.tsx
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';

// --- TYPES ---
export interface GiftInfo {
  recipient_name: string;
  recipient_email: string;
  is_gift: boolean;
  message?: string;
  send_date?: string;
  offer_title: string; 
}

export type CartCatalogSource =
  | 'shop_products'
  | 'boutique_forfaits'
  | 'presale_offers';

export type CartItem = {
  /** Identifiant unique de ligne panier. Ne remplace pas l'id produit. */
  cart_item_id?: string;

  /**
   * Catalogue autorisé ayant produit l'article.
   * Cette valeur aide le serveur à choisir un résolveur autorisé,
   * mais le serveur doit toujours relire et valider la ligne en base.
   */
  source_table?: CartCatalogSource;

  /** Identifiant de la ligne officielle dans le catalogue source. */
  source_id?: string;

  id: string; 
  type: 'product' | 'last_call' | 'boutique_forfait'; 
  name?: string; 
  title?: string; 
  forfait_title?: string; 
  full_title?: string; 
  price: number; 
  original_price?: number; 
  quantity: number; 
  image?: string | null; 
  image_url?: string | null; 
  business_id?: string; 
  business_name?: string; 
  weight_kg?: number; 
  last_call_public_id?: string; 
  is_gift?: boolean; 
  gift_info?: GiftInfo | null; 
  created_at?: string;
};

interface CartContextType {
  cart: CartItem[];
  cartItems: CartItem[];

  // Structure financière Premium et Légale (Québec)
  cartTotal: number; // Rétro-compatibilité (Sous-total)
  cartSubTotal: number; // Sous-total exact
  cartTaxes: number; // TPS + TVQ (14.975%)
  cartFinalTotal: number; // Montant estimé à facturer sur Stripe, hors livraison checkout

  cartCount: number;
  cartWeight: number; 

  addToCart: (item: Partial<CartItem>) => void;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, qty: number) => void;
  clearCart: () => void;

  isCartOpen: boolean;
  setIsCartOpen: (v: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const LS_KEY = 'forfeo_cart';
const FALLBACK_IMAGE = 'https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnKJPvD4DtyI_4A0qG3i22ff4r_R4i898X755x0vf98=';

const createCartLineId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `cart_${crypto.randomUUID()}`;
  }

  return `cart_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

const normalizeLegacyCartItem = (item: any): CartItem | null => {
  if (!item?.id) return null;

  return {
    ...item,
    cart_item_id: item.cart_item_id || createCartLineId(),
    source_table: item.source_table,
    source_id: item.source_id || item.id,
    price: Number(item.price) || 0,
    quantity: Math.max(1, Number(item.quantity) || 1),
    weight_kg: Number(item.weight_kg) || 0.5,
    image: item.image || item.image_url || FALLBACK_IMAGE,
    image_url: item.image_url || item.image || FALLBACK_IMAGE,
    created_at: item.created_at || new Date().toISOString(),
  };
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      const parsed = saved ? JSON.parse(saved) : [];
      if (Array.isArray(parsed)) {
        return parsed
          .map(normalizeLegacyCartItem)
          .filter(Boolean) as CartItem[];
      }
      return [];
    } catch {
      return [];
    }
  });

  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(cart));
    } catch {
      // ignore
    }
  }, [cart]);

  useEffect(() => {
    const docEl = document.documentElement;
    const body = document.body;

    const prevDocOverflow = docEl.style.overflow;
    const prevBodyOverflow = body.style.overflow;

    if (isCartOpen) {
      docEl.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
    } else {
      docEl.style.overflow = prevDocOverflow || '';
      body.style.overflow = prevBodyOverflow || '';
    }

    return () => {
      docEl.style.overflow = prevDocOverflow || '';
      body.style.overflow = prevBodyOverflow || '';
    };
  }, [isCartOpen]);

  const normalizeItem = useCallback((item: Partial<CartItem>): CartItem => {
    const id = item.id || 'unknown';
    const source_table = item.source_table;
    const source_id = item.source_id || id;
    const type = item.type || 'product';
    const name = item.name || item.title || 'Article';
    const title = item.title || item.name || name;
    const price = Number(item.price) || 0;
    const original_price = item.original_price !== undefined ? Number(item.original_price) || 0 : undefined;
    const image = item.image || item.image_url || FALLBACK_IMAGE;
    const business_id = item.business_id;
    const business_name = item.business_name;
    const weight_kg = Number(item.weight_kg) || 0.5;
    const last_call_public_id = item.last_call_public_id || item.id || undefined;

    return {
      cart_item_id: item.cart_item_id || createCartLineId(),
      source_table,
      source_id,
      id,
      type,
      name,
      title,
      forfait_title: item.forfait_title,
      full_title: item.full_title,
      price,
      original_price,
      quantity: Math.max(1, Number(item.quantity) || 1),
      image,
      image_url: item.image_url || image || '',
      business_id,
      business_name,
      weight_kg,
      last_call_public_id,
      is_gift: item.is_gift || false,
      gift_info: item.gift_info || null,
      created_at: item.created_at || new Date().toISOString()
    };
  }, []);

  const addToCart = useCallback(
    (item: Partial<CartItem>) => {
      setCart((prev) => {
        const incoming = normalizeItem(item);

        // Les cadeaux restent des lignes uniques pour éviter de mélanger destinataires/messages.
        if (incoming.is_gift) return [...prev, incoming];

        const existingIndex = prev.findIndex((i) =>
          i.id === incoming.id &&
          i.type === incoming.type &&
          !i.is_gift &&
          !incoming.cart_item_id?.startsWith('cart_forced_unique_')
        );

        if (existingIndex > -1) {
          const newCart = [...prev];
          newCart[existingIndex] = { 
            ...newCart[existingIndex], 
            quantity: newCart[existingIndex].quantity + incoming.quantity,
            business_id: newCart[existingIndex].business_id || incoming.business_id 
          };
          return newCart;
        }

        return [...prev, incoming];
      });
      setIsCartOpen(true);
    },
    [normalizeItem]
  );

  const matchesCartLine = (item: CartItem, itemId: string) => {
    if (item.cart_item_id === itemId) return true;

    // Rétrocompatibilité : si un ancien composant envoie encore id, on agit seulement sur les anciennes lignes sans cart_item_id.
    return !item.cart_item_id && item.id === itemId;
  };

  const removeFromCart = useCallback((itemId: string) => {
    setCart((prev) => prev.filter((i) => !matchesCartLine(i, itemId)));
  }, []);

  const updateQuantity = useCallback((itemId: string, qty: number) => {
    setCart((prev) =>
      prev.map((i) => {
        if (!matchesCartLine(i, itemId)) return i;
        if (i.is_gift) return { ...i, quantity: 1 };

        let nextQty = qty;
        if (qty === 1 || qty === -1) nextQty = i.quantity + qty;
        if (nextQty < 1) return i;
        return { ...i, quantity: nextQty };
      })
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
  }, []);

  // --- CALCULS FINANCIERS (Québec Strict) ---
  const cartSubTotal = useMemo(() => cart.reduce((acc, item) => acc + item.price * item.quantity, 0), [cart]);
  const cartTaxes = useMemo(() => cartSubTotal * 0.14975, [cartSubTotal]);
  const cartFinalTotal = useMemo(() => cartSubTotal + cartTaxes, [cartSubTotal, cartTaxes]);

  const cartCount = useMemo(() => cart.reduce((acc, item) => acc + item.quantity, 0), [cart]);
  const cartWeight = useMemo(() => cart.reduce((acc, item) => acc + (item.weight_kg || 0.5) * item.quantity, 0), [cart]);

  return (
    <CartContext.Provider
      value={{
        cart,
        cartItems: cart, 
        cartTotal: cartSubTotal,
        cartSubTotal,
        cartTaxes,
        cartFinalTotal,
        cartCount,
        cartWeight,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        isCartOpen,
        setIsCartOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};
